import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import TrainerLayout from './TrainerLayout';
import { GruppenInfo } from './TrainerSuche';
import { TrainerAntragKarte } from './TrainerAntrag';

function fmtDatum(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-DE');
}

export default function TrainerSchwimmer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggling, setToggling] = useState({});

  const load = useCallback(() => {
    // Beim Wechsel zu einem anderen Kind (z.B. über die Suche) alte Daten nicht stehen lassen
    setLoading(true);
    setData(null);
    setError('');
    api.get(`/api/trainer/schwimmer/${id}/fortschritt`)
      .then(r => setData(r.data))
      .catch(() => setError('Schwimmer-Daten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (uebungId, istAbgeschlossen) => {
    setToggling(t => ({ ...t, [uebungId]: true }));
    setError('');
    try {
      if (istAbgeschlossen) {
        await api.delete(`/api/trainer/schwimmer/${id}/uebungen/${uebungId}`);
      } else {
        await api.post(`/api/trainer/schwimmer/${id}/uebungen/${uebungId}`);
      }
      setData(d => ({
        ...d,
        uebungen: d.uebungen.map(u =>
          u.id === uebungId
            ? { ...u, abgeschlossen_am: istAbgeschlossen ? null : new Date().toISOString() }
            : u
        ),
      }));
    } catch {
      setError('Konnte nicht gespeichert werden');
    } finally {
      setToggling(t => ({ ...t, [uebungId]: false }));
    }
  };

  const abgeschlossen = (data?.uebungen || []).filter(u => u.abgeschlossen_am).length;
  const gesamt = (data?.uebungen || []).length;
  const prozent = gesamt > 0 ? Math.round((abgeschlossen / gesamt) * 100) : 0;

  return (
    <TrainerLayout
      title={data ? `${data.schwimmer.vorname} ${data.schwimmer.nachname}` : 'Schwimmer'}
      backTo={-1}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {data && !loading && (
        <>
          {/* Schwimmer-Info */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: '1rem',
            marginBottom: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.25rem' }}>
              {data.schwimmer.vorname} {data.schwimmer.nachname}
              {data.schwimmer.aktiv_im_verein === false && (
                <span style={{ marginLeft: 8, fontSize: '0.7rem', fontWeight: 700, color: '#78909c',
                  background: '#eceff1', borderRadius: 10, padding: '1px 8px', verticalAlign: 'middle' }}>ehemalig</span>
              )}
            </div>
            {data.schwimmer.geburtsdatum && (
              <div style={{ fontSize: '0.82rem', color: '#666' }}>
                * {fmtDatum(data.schwimmer.geburtsdatum)}
              </div>
            )}

            {/* Gruppe, Trainer, Ort, Zeiten */}
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f0f0f0' }}>
              <GruppenInfo gruppen={data.schwimmer.gruppen} />
            </div>

            {/* Notfallkontakt – Telefon prominent */}
            {data.schwimmer.kontakt_telefon && (
              <a href={`tel:${data.schwimmer.kontakt_telefon}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  marginTop: '0.75rem', padding: '0.625rem 0.875rem',
                  background: '#fff3e0', borderRadius: 8,
                  border: '1.5px solid #ffb74d',
                  textDecoration: 'none', color: '#e65100',
                  fontWeight: 700, fontSize: '1rem',
                }}>
                <span style={{ fontSize: '1.25rem' }}>📞</span>
                <div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 400, color: '#bf360c', marginBottom: '0.1rem' }}>
                    NOTFALLKONTAKT
                  </div>
                  {data.schwimmer.kontakt_telefon}
                </div>
              </a>
            )}
          </div>

          {/* Aufnahmeantrag */}
          <TrainerAntragKarte schwimmer={data.schwimmer}
            onGeaendert={(stand) => setData(d => ({ ...d, schwimmer: { ...d.schwimmer, ...stand } }))} />

          {/* Fortschrittsbalken */}
          {gesamt > 0 && (
            <div style={{
              background: '#fff', borderRadius: 12, padding: '1rem',
              marginBottom: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Fortschritt</span>
                <span style={{ fontWeight: 700, color: prozent === 100 ? '#2e7d32' : '#1565c0' }}>
                  {abgeschlossen}/{gesamt} ({prozent}%)
                </span>
              </div>
              <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${prozent}%`,
                  background: prozent === 100 ? '#4caf50' : '#1565c0',
                  borderRadius: 5, transition: 'width 0.3s',
                }} />
              </div>
            </div>
          )}

          {/* Übungen */}
          {gesamt === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#999', background: '#fff', borderRadius: 12 }}>
              Keine Prüfungsanforderungen für diesen Kurstyp definiert
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              {data.uebungen.map((u, i) => {
                const done = !!u.abgeschlossen_am;
                const loading = toggling[u.id];
                return (
                  <div key={u.id}
                    onClick={() => !loading && handleToggle(u.id, done)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '1rem',
                      padding: '1rem',
                      borderBottom: i < data.uebungen.length - 1 ? '1px solid #f5f5f5' : 'none',
                      cursor: 'pointer',
                      background: done ? '#f9fff9' : '#fff',
                      transition: 'background 0.15s',
                    }}>
                    {/* Großes Checkbox-Icon */}
                    <div style={{
                      width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: done ? '#4caf50' : '#f0f0f0',
                      border: `2px solid ${done ? '#4caf50' : '#ddd'}`,
                      fontSize: '1.4rem', transition: 'all 0.15s',
                    }}>
                      {loading ? '…' : done ? '✓' : ''}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: '0.95rem',
                        textDecoration: done ? 'line-through' : 'none',
                        color: done ? '#888' : '#222',
                      }}>
                        {u.titel}
                      </div>
                      {u.beschreibung && (
                        <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.1rem' }}>
                          {u.beschreibung}
                        </div>
                      )}
                      {done && u.abgeschlossen_am && (
                        <div style={{ fontSize: '0.72rem', color: '#4caf50', fontWeight: 600, marginTop: '0.2rem' }}>
                          ✓ {fmtDatum(u.abgeschlossen_am)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Zur Stammdaten-Seite */}
          <button
            onClick={() => navigate(`/schwimmer/${id}`)}
            style={{
              width: '100%', marginTop: '1rem', padding: '0.75rem',
              background: 'none', border: '1px solid #e0e0e0', borderRadius: 10,
              cursor: 'pointer', fontSize: '0.85rem', color: '#1565c0',
            }}>
            → Stammdaten bearbeiten
          </button>
        </>
      )}
    </TrainerLayout>
  );
}
